import { Navigate, useLocation } from 'react-router-dom';
import type { ReactElement } from 'react';
import { resolveGameIdFromUrl } from './tenant-resolver';

/**
 * DK iframe entry gate (DKPLAT-467).
 *
 * DK loads each game from a single host with a path segment + query params:
 *   freetoplay.draftkings.com/pickem?gameId=<guid>&tenantId=<slug>
 *   freetoplay.draftkings.com/dailydraw?gameId=…   (daily reveal)
 *   freetoplay.draftkings.com/freetoplay?gameId=…  (squares)
 *
 * Depending on DK's proxy, our app receives either the segment path
 * (e.g. /pickem) or a stripped root (/). This gate covers both: when a game id
 * is present (path OR ?gameId=), it deep-links to the existing /game/:gameId
 * route, preserving the query string (token, tenantId, locale, …). Otherwise it
 * renders the normal app.
 *
 * Tenant (?tenantId=) is resolved separately at module load by
 * resolveTenantFromUrl() and flows into the X-Tenant-Slug header.
 *
 * Shared by pickem, squares, and daily-reveal (each mounts it on its root and
 * DK-segment routes). Vendored into every app via `pnpm vendor:sync`.
 */
export default function GameEntry({ children }: { children: ReactElement }) {
  const location = useLocation();
  const gameId = resolveGameIdFromUrl();
  if (gameId) {
    return <Navigate replace to={`/game/${gameId}${location.search}`} />;
  }
  return children;
}
